import type { DemoReadyPayload } from "../types/demoEntryAnalysis";
import type { Report } from "../types/reports";
import {
  cohortHeadlineFromNode,
  getConditionAnalysisRoot,
} from "./conditionAnalysisSelectors";
import { looksLikeTradeStatisticsAggregate } from "./extractReportWindowTradeStatistics";

function pickYmd(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    if (typeof c !== "string") continue;
    const t = c.trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
  }
  return undefined;
}

/** Prefer explicit BE window fields; fall back to snapshot/meta nested shapes. */
export function extractDemoAnalysisWindow(payload: DemoReadyPayload): {
  start_date?: string;
  end_date?: string;
  timezone?: string;
} {
  const meta = payload.demo_meta;
  const snap =
    payload.input_snapshot && typeof payload.input_snapshot === "object"
      ? (payload.input_snapshot as Record<string, unknown>)
      : {};
  const snapMeta =
    snap.meta && typeof snap.meta === "object" && !Array.isArray(snap.meta)
      ? (snap.meta as Record<string, unknown>)
      : {};
  const period =
    (snap.period && typeof snap.period === "object" && !Array.isArray(snap.period)
      ? (snap.period as Record<string, unknown>)
      : null) ||
    (snap.cohort_period && typeof snap.cohort_period === "object" && !Array.isArray(snap.cohort_period)
      ? (snap.cohort_period as Record<string, unknown>)
      : null) ||
    (snapMeta.period && typeof snapMeta.period === "object" && !Array.isArray(snapMeta.period)
      ? (snapMeta.period as Record<string, unknown>)
      : null);

  const start_date = pickYmd(
    meta?.window_start,
    meta?.start_date,
    meta?.local_start_date,
    payload.start_date,
    payload.window_start,
    snap.start_date,
    snap.window_start,
    snap.local_start_date,
    snapMeta.start_date,
    snapMeta.window_start,
    snapMeta.local_start_date,
    period?.local_start_date,
    period?.start_date,
    period?.start
  );
  const end_date = pickYmd(
    meta?.window_end,
    meta?.end_date,
    meta?.local_end_date,
    payload.end_date,
    payload.window_end,
    snap.end_date,
    snap.window_end,
    snap.local_end_date,
    snapMeta.end_date,
    snapMeta.window_end,
    snapMeta.local_end_date,
    period?.local_end_date,
    period?.end_date,
    period?.end
  );
  const tzCandidate =
    (typeof meta?.timezone === "string" && meta.timezone.trim()) ||
    (typeof snap.timezone === "string" && snap.timezone.trim()) ||
    (typeof snapMeta.timezone === "string" && snapMeta.timezone.trim()) ||
    (typeof period?.timezone === "string" && period.timezone.trim()) ||
    "";

  return {
    ...(start_date ? { start_date } : {}),
    ...(end_date ? { end_date } : {}),
    ...(tzCandidate ? { timezone: tzCandidate } : {}),
  };
}

/**
 * When the demo payload omits `/trades/statistics`, synthesize a minimal aggregate
 * from the All-trades condition_analysis headline so OverviewMetrics can render.
 */
export function synthesizeTradeStatisticsFromConditionAnalysis(
  inputSnapshot: unknown,
  analysisEvidence?: unknown
): Record<string, unknown> | null {
  const root =
    getConditionAnalysisRoot(inputSnapshot) ||
    (analysisEvidence != null
      ? getConditionAnalysisRoot({ analysis_evidence: analysisEvidence })
      : null) ||
    (analysisEvidence != null
      ? getConditionAnalysisRoot({ stats: { condition_analysis: analysisEvidence } })
      : null);
  if (!root) return null;
  const headline = cohortHeadlineFromNode(root);
  if (headline.sample == null && headline.winRate == null && headline.totalPnl == null) {
    return null;
  }
  const total = Math.max(0, Math.round(headline.sample ?? 0));
  const winRate = headline.winRate ?? 0;
  const winning =
    total > 0 && Number.isFinite(winRate)
      ? Math.min(total, Math.max(0, Math.round((winRate / 100) * total)))
      : 0;
  const totalPnl = headline.totalPnl ?? 0
  const expectancy =
    headline.expectancy != null && Number.isFinite(headline.expectancy)
      ? headline.expectancy
      : total > 0
        ? totalPnl / total
        : 0
  return {
    total_trades: total,
    open_trades: 0,
    closed_trades: total,
    winning_trades: winning,
    losing_trades: Math.max(0, total - winning),
    win_rate: winRate,
    total_pnl: totalPnl,
    average_win: 0,
    average_loss: 0,
    largest_win: 0,
    largest_loss: 0,
    total_return_percent: 0,
    average_return_percent: 0,
    most_traded_symbol: null,
    most_traded_symbol_count: null,
    expectancy,
    average_per_trade_pnl: expectancy,
    portfolio_value: null,
  };
}

/** Adapt a demo ready payload into a minimal Report shape for Overview / Market reuse. */
export function demoPayloadToReport(payload: DemoReadyPayload): Report {
  const symbol =
    payload.demo_meta?.symbol ||
    payload.symbol ||
    (payload.input_snapshot as { meta?: { symbol?: string } } | undefined)?.meta?.symbol ||
    "";
  const candle =
    payload.demo_meta?.candle_timeframe ||
    payload.candle_timeframe ||
    (payload.input_snapshot as { meta?: { candle_timeframe?: string } } | undefined)?.meta
      ?.candle_timeframe ||
    "1h";

  const stats = {
    ...(payload.stats && typeof payload.stats === "object" ? payload.stats : {}),
  } as Record<string, unknown>;
  if (payload.analysis_evidence != null && stats.condition_analysis == null) {
    // Some BE shapes put tables under analysis_evidence; Market UI reads condition_analysis.
    if (
      typeof payload.analysis_evidence === "object" &&
      payload.analysis_evidence &&
      !Array.isArray(payload.analysis_evidence)
    ) {
      const ae = payload.analysis_evidence as Record<string, unknown>;
      if ("condition_analysis" in ae) {
        stats.condition_analysis = ae.condition_analysis;
      } else if (
        "regime_breakdown" in ae ||
        "regime_summary" in ae ||
        "directional_condition_analysis" in ae
      ) {
        stats.condition_analysis = ae;
      }
    }
  }

  // Surface trade-window aggregates where OverviewMetrics / extractReportWindowTradeStatistics look.
  const snapshotIn =
    payload.input_snapshot && typeof payload.input_snapshot === "object"
      ? { ...(payload.input_snapshot as Record<string, unknown>) }
      : {};
  const nestedStats =
    snapshotIn.stats && typeof snapshotIn.stats === "object" && !Array.isArray(snapshotIn.stats)
      ? { ...(snapshotIn.stats as Record<string, unknown>) }
      : {};
  const mergedStats: Record<string, unknown> = { ...nestedStats, ...stats };

  let tradeStatistics: Record<string, unknown> | null = looksLikeTradeStatisticsAggregate(mergedStats)
    ? mergedStats
    : null;
  if (!tradeStatistics) {
    for (const key of [
      "trade_statistics",
      "tradeStatistics",
      "summary",
      "overview",
      "totals",
      "aggregated",
      "bundle_statistics",
      "bundleStatistics",
    ] as const) {
      const candidate = mergedStats[key];
      if (looksLikeTradeStatisticsAggregate(candidate)) {
        tradeStatistics = candidate;
        break;
      }
    }
  }

  const windowDates = extractDemoAnalysisWindow(payload);

  const input_snapshot: Record<string, unknown> = {
    ...snapshotIn,
    stats: mergedStats,
    meta: {
      ...((snapshotIn.meta as Record<string, unknown> | undefined) || {}),
      symbol: String(symbol || ""),
      candle_timeframe: String(candle),
      ...(windowDates.start_date ? { start_date: windowDates.start_date } : {}),
      ...(windowDates.end_date ? { end_date: windowDates.end_date } : {}),
      ...(windowDates.timezone ? { timezone: windowDates.timezone } : {}),
    },
  };
  if (!tradeStatistics) {
    tradeStatistics = synthesizeTradeStatisticsFromConditionAnalysis(
      { ...input_snapshot, analysis_evidence: payload.analysis_evidence },
      payload.analysis_evidence
    );
  }

  if (tradeStatistics) {
    input_snapshot.trade_statistics = tradeStatistics;
    if (tradeStatistics !== mergedStats) {
      input_snapshot.stats = {
        ...(mergedStats as Record<string, unknown>),
        trade_statistics: tradeStatistics,
      };
    }
  }

  const now = new Date().toISOString();
  return {
    id: "demo-entry-analysis",
    report_type: payload.report_type || "market-context-performance",
    status: "published",
    title: payload.title || "Demo Entry Analysis",
    symbol: String(symbol || ""),
    candle_timeframe: String(candle),
    timeframe: "last-30-days",
    start_date: windowDates.start_date,
    end_date: windowDates.end_date,
    findings: payload.findings ?? [],
    narrative: payload.narrative_markdown || payload.narrative || "",
    narrative_markdown: payload.narrative_markdown || payload.narrative || "",
    finding_schema_version: payload.finding_schema_version,
    input_snapshot,
    analysis_evidence: payload.analysis_evidence,
    created_at: now,
    updated_at: now,
  } as Report;
}
